import { vec3 } from 'gl-matrix';
import { Overlay, type OverlayQuad } from './Overlay';
import type { MsdfFont } from './MsdfFont';
import { layoutText, type LayoutResult } from './TextLayout';

export type TextAlign = 'left' | 'center' | 'right';

export interface TextOverlayOptions {
  font: MsdfFont;
  text: string;
  /** Display-pixel line height. Defaults to 16. */
  fontSize?: number;
  /** RGBA, alpha multiplies the output. Defaults to opaque white. */
  color?: [number, number, number, number];
  /** Horizontal anchor alignment. Default 'center'. */
  align?: TextAlign;
  /** Pixel offset relative to projected world anchor (+Y is down). Default [0, 0]. */
  screenOffsetX?: number;
  screenOffsetY?: number;
  /** When true, occludes behind world geometry. Default false. */
  depthTested?: boolean;
}

/**
 * One line of MSDF text anchored at a world position with optional pixel
 * offset. Re-layouts on demand (set `text` / `fontSize` and call `invalidate`).
 *
 * Memoizes the laid-out glyph list AND pools per-glyph {@link OverlayQuad}
 * objects + their inner tuples so per-frame emit is zero-allocation after
 * the first frame at a given glyph count.
 */
export class TextOverlay extends Overlay {
  public readonly font: MsdfFont;
  private _text: string;
  private _fontSize: number;
  public color: [number, number, number, number];
  public align: TextAlign;
  public screenOffsetX: number;
  public screenOffsetY: number;
  public depthTested: boolean;

  private layoutCache: LayoutResult | null = null;
  /** Reused worldPos scratch shared by every pooled quad — re-stamped each emit. */
  private readonly worldPosScratch: vec3 = vec3.create();
  /** Pool of OverlayQuad objects grown to fit `layout.glyphs.length`. Inner tuples are pre-allocated and mutated in place. */
  private readonly quadPool: OverlayQuad[] = [];

  constructor(opts: TextOverlayOptions) {
    super();
    this.font = opts.font;
    this._text = opts.text;
    this._fontSize = opts.fontSize ?? 16;
    this.color = opts.color ?? [1, 1, 1, 1];
    this.align = opts.align ?? 'center';
    this.screenOffsetX = opts.screenOffsetX ?? 0;
    this.screenOffsetY = opts.screenOffsetY ?? 0;
    this.depthTested = opts.depthTested ?? false;
  }

  public get text(): string {
    return this._text;
  }
  public set text(v: string) {
    if (v === this._text) return;
    this._text = v;
    this.layoutCache = null;
  }

  public get fontSize(): number {
    return this._fontSize;
  }
  public set fontSize(v: number) {
    if (v === this._fontSize) return;
    this._fontSize = v;
    this.layoutCache = null;
  }

  /** Force a fresh layout on next emit. */
  public invalidate(): void {
    this.layoutCache = null;
  }

  public emit(out: OverlayQuad[]): void {
    if (this._text.length === 0) return;
    if (!this.layoutCache) {
      this.layoutCache = layoutText(this.font, this._text, { fontSize: this._fontSize });
    }
    const layout = this.layoutCache;
    if (layout.glyphs.length === 0) return;

    this.ensurePoolSize(layout.glyphs.length);
    this.resolvePosition(this.worldPosScratch);

    // Horizontal alignment: shift the text's x=0 origin so it lines up with
    // the desired anchor. The vertical anchor is the top of the line (so a
    // typical "above the head" overlay uses a negative screenOffsetY).
    let originX = this.screenOffsetX;
    if (this.align === 'center') originX -= layout.width * 0.5;
    else if (this.align === 'right') originX -= layout.width;
    const originY = this.screenOffsetY;

    for (let i = 0; i < layout.glyphs.length; i++) {
      const g = layout.glyphs[i];
      const q = this.quadPool[i];
      // Re-stamp mutable references — caller may have swapped color or
      // toggled depthTested since the pool entry was first created.
      q.color = this.color;
      q.depthTested = this.depthTested;
      q.font = this.font;
      // Per-glyph values updated in place — no allocations.
      q.sizePx[0] = g.width;
      q.sizePx[1] = g.height;
      // OverlayQuad screenOffsetPx is the CENTER of the quad, so add half the
      // glyph size to convert BMFont's top-left convention to center.
      q.screenOffsetPx[0] = originX + g.x + g.width * 0.5;
      q.screenOffsetPx[1] = originY + g.y + g.height * 0.5;
      q.uvOrigin![0] = g.uvX;
      q.uvOrigin![1] = g.uvY;
      q.uvSize![0] = g.uvW;
      q.uvSize![1] = g.uvH;
      // Clear last frame's fade — the manager re-stamps when an overlay sits
      // in the fade band, otherwise the pool would retain the prior value.
      q.alphaScale = undefined;
      out.push(q);
    }
  }

  private ensurePoolSize(n: number): void {
    while (this.quadPool.length < n) {
      this.quadPool.push({
        // All pooled quads share the instance's worldPos scratch — the anchor
        // is identical across every glyph in one emit call.
        worldPos: this.worldPosScratch,
        sizePx: [0, 0],
        screenOffsetPx: [0, 0],
        color: this.color,
        depthTested: this.depthTested,
        font: this.font,
        uvOrigin: [0, 0],
        uvSize: [0, 0],
      });
    }
  }
}
